/**
 * Service Worker Registration + Update Detection
 * Spec ref: SPEC-025 §3.2 (SW Registration),
 *           SPEC-NEGOCIO §9.1 (Fail-closed)
 *           F-24: Register compiled /sw.js (built from sw.ts via workbox)
 *
 * SW scope: / (not /app/) — caches all pages including public landing
 * API routes are excluded from caching via workbox denylist in sw.ts
 * Update check: every 60 minutes
 * On update: show toast to user
 *
 * IMPORTANT: The service worker source is public/sw.ts which must be
 * compiled to sw.js before deployment. The build script handles this:
 *   npx workbox-cli injectManifest or tsc bundling of sw.ts → sw.js
 * The registration below always references the compiled /sw.js output.
 *
 * BUG-EXP5-023 (SPEC-041 QA audit pre-launch): added
 * `unregisterServiceWorker()` export. Called by `useAuth.logout()` (via
 * the `profileia:logged-out` event listener in app-shell or directly)
 * to wipe cached HTML that may contain stale user-specific data after
 * logout. Without this, a re-login as a DIFFERENT user in the same
 * browser could serve the previous user's cached `/app/*` HTML until
 * the SW's own TTL expired (edge-case data leakage cross-user).
 *
 * AUDIT3-SHARED-018 (SPEC-046 QA AUDIT R1 IA — BATCH-6): the periodic
 * `setInterval(registration.update, 60min)` interval is now stored in a
 * module-level variable (`updateInterval`) and cleared on re-registration
 * (or on hot-reload). Pre-fix, the interval stacked on every HMR /
 * remount — each re-registration added a new 60min interval that all
 * fired in parallel, multiplying the SW update-check traffic.
 *
 * AUDIT3-SHARED-019 (SPEC-046 QA AUDIT R1 IA — BATCH-6): the
 * `statechange` listener now tracks `wasActive = !!registration.active`
 * BEFORE the `updatefound` event fires, and only shows the update toast
 * when `wasActive && newWorker.state === 'activated'`. Pre-fix, the
 * toast fired on the FIRST install (no previous active worker existed),
 * giving the false impression that an "update" was available when the
 * SW was just installing for the first time.
 */

import { toast } from 'sonner';

/** Path to compiled service worker — F-24: always reference build output */
const SW_URL = '/sw.js';

/**
 * AUDIT3-SHARED-018: module-level handle for the periodic update
 * interval. Stored so re-registration (HMR, PwaProvider remount) can
 * clear the previous interval before scheduling a new one — otherwise
 * intervals stack and the SW update-check traffic multiplies.
 */
let updateInterval: ReturnType<typeof setInterval> | null = null;

function showUpdateToast(): void {
  toast.info('Nueva versión disponible', {
    description: 'Recarga la página para obtener la última versión.',
    action: {
      label: 'Recargar',
      onClick: () => window.location.reload(),
    },
    duration: 10000,
  });
}

export function registerServiceWorker(): void {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;

  window.addEventListener('load', async () => {
    try {
      const registration = await navigator.serviceWorker.register(SW_URL, {
        scope: '/',
      });

      // AUDIT3-SHARED-018: clear any previous interval before scheduling
      // a new one. Prevents interval stacking on HMR / PwaProvider remount.
      if (updateInterval !== null) {
        clearInterval(updateInterval);
        updateInterval = null;
      }
      // Check for updates every 60 minutes.
      updateInterval = setInterval(() => {
        void registration.update();
      }, 60 * 60 * 1000);

      // AUDIT3-SHARED-019: capture `wasActive` BEFORE `updatefound` fires.
      // The first install has `registration.active === null` (no previous
      // worker) → `wasActive === false` → no toast on the first `activated`
      // state. Subsequent updates have `registration.active !== null`
      // (previous worker still active) → `wasActive === true` → toast.
      const wasActive = !!registration.active;

      // Listen for updates
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (!newWorker) return;

        newWorker.addEventListener('statechange', () => {
          // AUDIT3-SHARED-019: only show the toast when this is a REAL
          // update (wasActive === true) AND the new worker has finished
          // activating. Pre-fix, the toast fired on the first install
          // too — misleading UX ("Nueva versión disponible" on first
          // ever page load when nothing had been updated).
          if (wasActive && newWorker.state === 'activated') {
            // New version available — show update toast
            showUpdateToast();
          }
        });
      });
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('SW registration failed:', error);
    }
  });
}

/**
 * BUG-EXP5-023: Unregister all service workers + clear SW-owned caches.
 *
 * Called on logout (via `useAuth.logout()` → `profileia:logged-out`
 * event) to wipe any cached HTML that may contain stale user-specific
 * data (e.g., SSR'd `/app/*` pages with the previous user's profile
 * info embedded). Without this, a re-login as a different user in the
 * same browser could serve the previous user's cached pages until the
 * SW's own TTL expired — an edge-case data leakage vector.
 *
 * The SW will re-register on the next page load via `registerServiceWorker()`
 * (called by PwaProvider on mount), so this is a transient wipe, not a
 * permanent disable.
 *
 * AUDIT3-SHARED-018: also clears the `updateInterval` so a fresh
 * registration starts with a clean interval handle (the new
 * registration will schedule its own interval).
 *
 * Safe to call on the server (no-op) — guards `typeof window` check.
 */
export async function unregisterServiceWorker(): Promise<void> {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;
  try {
    // AUDIT3-SHARED-018: clear the periodic update interval before
    // unregistering — otherwise the interval would keep firing
    // `registration.update()` on a now-unregistered SW (no-op, but
    // wasted CPU + a confusing "update found" race if a new
    // registration happens to land before the next interval tick).
    if (updateInterval !== null) {
      clearInterval(updateInterval);
      updateInterval = null;
    }
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((r) => r.unregister()));
    // Clear SW-owned caches (Cache Storage API). The SW may have stored
    // responses under workbox-precache-v2, runtime-caches, etc.
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch (error) {
    // Best-effort — don't block logout on SW cleanup failure.
    if (process.env.NODE_ENV === 'development') {
      console.error('SW unregister failed:', error);
    }
  }
}
